// ============================================================
// Client Custom Fields Config — industry → field definitions
// Keys match INDUSTRY_CATEGORIES[].id from industryTaxonomy.ts
// Same pattern as navigationConfig.ts / scheduleConfig.ts
// ============================================================

export interface ClientFieldDef {
  key: string;           // stored in custom_fields JSONB
  label: string;         // display label
  type: 'text' | 'textarea' | 'select' | 'date' | 'boolean' | 'number';
  placeholder?: string;
  options?: string[];    // for select type
  section?: string;      // group header
  required?: boolean;    // false by default
  icon?: string;         // lucide icon name for display
}

const CLIENT_FIELDS: Record<string, ClientFieldDef[]> = {

  // ── Beauty & Personal Care ──
  beauty: [
    { key: 'preferred_operator', label: 'Preferred stylist', type: 'text', placeholder: 'Who do they usually see?', section: 'Preferences', icon: 'User' },
    { key: 'hair_type', label: 'Hair type', type: 'select', options: ['Straight', 'Wavy', 'Curly', 'Coily', 'Fine', 'Thick'], section: 'Hair Profile', icon: 'Scissors' },
    { key: 'hair_colour', label: 'Current colour', type: 'text', placeholder: 'e.g. Level 7 golden blonde', section: 'Hair Profile', icon: 'Palette' },
    { key: 'colour_formula', label: 'Colour formula', type: 'textarea', placeholder: 'Mix details, developer volume, timing...', section: 'Hair Profile', icon: 'FlaskConical' },
    { key: 'allergies', label: 'Allergies or sensitivities', type: 'textarea', placeholder: 'Latex, specific products, etc.', section: 'Health', icon: 'AlertTriangle' },
    { key: 'skin_type', label: 'Skin type', type: 'select', options: ['Normal', 'Dry', 'Oily', 'Combination', 'Sensitive'], section: 'Health', icon: 'Droplets' },
    { key: 'nail_shape_pref', label: 'Preferred nail shape', type: 'select', options: ['Square', 'Round', 'Oval', 'Almond', 'Stiletto', 'Coffin', 'Squoval'], section: 'Preferences', icon: 'Gem' },
    { key: 'service_notes', label: 'Standing service notes', type: 'textarea', placeholder: 'Always wants extra gel coat, prefers no small talk, etc.', section: 'Preferences', icon: 'StickyNote' },
  ],

  // ── Pet Services ──
  pet: [
    { key: 'pet_name', label: 'Pet name', type: 'text', placeholder: 'Buddy, Luna, etc.', section: 'Pet Info', icon: 'PawPrint', required: true },
    { key: 'pet_species', label: 'Species', type: 'select', options: ['Dog', 'Cat', 'Bird', 'Rabbit', 'Reptile', 'Other'], section: 'Pet Info', icon: 'PawPrint' },
    { key: 'pet_breed', label: 'Breed', type: 'text', placeholder: 'e.g. Golden Retriever', section: 'Pet Info', icon: 'PawPrint' },
    { key: 'pet_weight', label: 'Weight (lbs)', type: 'number', placeholder: '45', section: 'Pet Info', icon: 'Scale' },
    { key: 'pet_age', label: 'Age', type: 'text', placeholder: 'e.g. 3 years', section: 'Pet Info', icon: 'Calendar' },
    { key: 'vaccinations', label: 'Vaccination status', type: 'textarea', placeholder: 'Rabies (exp 2027), DHPP, Bordetella...', section: 'Health', icon: 'Shield' },
    { key: 'medical_notes', label: 'Medical conditions', type: 'textarea', placeholder: 'Hip dysplasia, anxiety, medication...', section: 'Health', icon: 'Heart' },
    { key: 'grooming_notes', label: 'Grooming preferences', type: 'textarea', placeholder: 'Sensitive paws, dislikes dryer, specific cut style...', section: 'Preferences', icon: 'Scissors' },
    { key: 'emergency_contact', label: 'Emergency vet', type: 'text', placeholder: 'Clinic name + phone', section: 'Emergency', icon: 'Phone' },
  ],

  // ── Home Services & Repairs ──
  home_services: [
    { key: 'property_type', label: 'Property type', type: 'select', options: ['House', 'Condo', 'Townhouse', 'Apartment', 'Commercial'], section: 'Property', icon: 'Home' },
    { key: 'property_age', label: 'Property age', type: 'text', placeholder: 'e.g. Built 1985', section: 'Property', icon: 'Calendar' },
    { key: 'access_instructions', label: 'Access instructions', type: 'textarea', placeholder: 'Gate code, key location, dog in backyard...', section: 'Access', icon: 'Key' },
    { key: 'parking_notes', label: 'Parking notes', type: 'text', placeholder: 'Driveway okay, street parking only, etc.', section: 'Access', icon: 'Car' },
    { key: 'service_history_notes', label: 'Property notes', type: 'textarea', placeholder: 'Older pipes, knob-and-tube wiring, etc.', section: 'Property', icon: 'StickyNote' },
  ],

  // ── Cleaning Services ──
  cleaning: [
    { key: 'property_type', label: 'Property type', type: 'select', options: ['House', 'Condo', 'Apartment', 'Office', 'Airbnb/Rental'], section: 'Property', icon: 'Home' },
    { key: 'bedrooms', label: 'Bedrooms', type: 'number', placeholder: '3', section: 'Property', icon: 'Bed' },
    { key: 'bathrooms', label: 'Bathrooms', type: 'number', placeholder: '2', section: 'Property', icon: 'Bath' },
    { key: 'sqft', label: 'Approx square feet', type: 'number', placeholder: '1800', section: 'Property', icon: 'Ruler' },
    { key: 'access_instructions', label: 'Access instructions', type: 'textarea', placeholder: 'Lock box code, concierge, key under mat...', section: 'Access', icon: 'Key' },
    { key: 'cleaning_preferences', label: 'Cleaning preferences', type: 'textarea', placeholder: 'No bleach on marble, eco products only, pets present...', section: 'Preferences', icon: 'Sparkles' },
    { key: 'supplies_provided', label: 'Client provides supplies?', type: 'boolean', section: 'Preferences', icon: 'Package' },
  ],

  // ── Landscaping & Outdoor ──
  landscaping: [
    { key: 'property_type', label: 'Property type', type: 'select', options: ['Residential', 'Commercial', 'Strata/HOA', 'Municipal'], section: 'Property', icon: 'Home' },
    { key: 'lot_size', label: 'Lot size', type: 'text', placeholder: 'e.g. 50x120 ft', section: 'Property', icon: 'Ruler' },
    { key: 'irrigation', label: 'Irrigation system?', type: 'boolean', section: 'Property', icon: 'Droplets' },
    { key: 'access_instructions', label: 'Gate/access', type: 'textarea', placeholder: 'Gate code, side entry, dog in yard...', section: 'Access', icon: 'Key' },
    { key: 'special_areas', label: 'Special areas', type: 'textarea', placeholder: 'Flower beds, vegetable garden, pool area...', section: 'Property', icon: 'Flower' },
    { key: 'equipment_notes', label: 'Equipment notes', type: 'textarea', placeholder: 'Narrow gate (no ride-on), steep slope, etc.', section: 'Property', icon: 'Wrench' },
  ],

  // ── Automotive Services ──
  automotive: [
    { key: 'vehicle_make', label: 'Vehicle make', type: 'text', placeholder: 'e.g. Toyota', section: 'Vehicle', icon: 'Car' },
    { key: 'vehicle_model', label: 'Vehicle model', type: 'text', placeholder: 'e.g. Camry', section: 'Vehicle', icon: 'Car' },
    { key: 'vehicle_year', label: 'Year', type: 'number', placeholder: '2019', section: 'Vehicle', icon: 'Calendar' },
    { key: 'vehicle_colour', label: 'Colour', type: 'text', placeholder: 'Silver', section: 'Vehicle', icon: 'Palette' },
    { key: 'vin', label: 'VIN', type: 'text', placeholder: 'Optional', section: 'Vehicle', icon: 'Hash' },
    { key: 'licence_plate', label: 'Licence plate', type: 'text', placeholder: 'e.g. ABCD 123', section: 'Vehicle', icon: 'RectangleHorizontal' },
    { key: 'service_history', label: 'Service notes', type: 'textarea', placeholder: 'Last oil change, known issues, warranty status...', section: 'History', icon: 'StickyNote' },
  ],

  // ── Wellness & Fitness ──
  wellness: [
    { key: 'fitness_goals', label: 'Goals', type: 'textarea', placeholder: 'Weight loss, flexibility, strength, rehab...', section: 'Profile', icon: 'Target' },
    { key: 'injuries', label: 'Injuries or limitations', type: 'textarea', placeholder: 'Bad knee, lower back, shoulder surgery...', section: 'Health', icon: 'AlertTriangle' },
    { key: 'experience_level', label: 'Experience level', type: 'select', options: ['Beginner', 'Intermediate', 'Advanced'], section: 'Profile', icon: 'BarChart' },
    { key: 'preferred_instructor', label: 'Preferred instructor', type: 'text', section: 'Preferences', icon: 'User' },
    { key: 'emergency_contact', label: 'Emergency contact', type: 'text', placeholder: 'Name + phone', section: 'Emergency', icon: 'Phone' },
  ],

  // ── Construction & Trades ──
  construction: [
    { key: 'property_type', label: 'Property type', type: 'select', options: ['Residential', 'Commercial', 'Industrial', 'Institutional'], section: 'Property', icon: 'Home' },
    { key: 'access_instructions', label: 'Site access', type: 'textarea', placeholder: 'Gate code, site supervisor contact, PPE required...', section: 'Access', icon: 'Key' },
    { key: 'parking_notes', label: 'Parking/loading', type: 'text', placeholder: 'Driveway, loading dock, street permit needed...', section: 'Access', icon: 'Car' },
    { key: 'permit_notes', label: 'Permit info', type: 'textarea', placeholder: 'Building permit #, inspection dates...', section: 'Compliance', icon: 'FileCheck' },
    { key: 'insurance_notes', label: 'Insurance requirements', type: 'textarea', placeholder: 'COI required, minimum coverage...', section: 'Compliance', icon: 'Shield' },
  ],

  // ── Property & Facilities ──
  property: [
    { key: 'unit_number', label: 'Unit number', type: 'text', placeholder: 'e.g. Unit 3B', section: 'Tenant', icon: 'Home' },
    { key: 'lease_start', label: 'Lease start', type: 'date', section: 'Tenant', icon: 'Calendar' },
    { key: 'lease_end', label: 'Lease end', type: 'date', section: 'Tenant', icon: 'Calendar' },
    { key: 'rent_amount', label: 'Monthly rent', type: 'number', placeholder: '1800', section: 'Tenant', icon: 'DollarSign' },
    { key: 'key_location', label: 'Key/access', type: 'text', placeholder: 'Lock box #4421, concierge desk...', section: 'Access', icon: 'Key' },
    { key: 'emergency_contact', label: 'Emergency contact', type: 'text', placeholder: 'Name + phone', section: 'Tenant', icon: 'Phone' },
    { key: 'pet_on_site', label: 'Pet on site?', type: 'boolean', section: 'Access', icon: 'PawPrint' },
    { key: 'parking_spot', label: 'Parking spot', type: 'text', placeholder: 'P1-42', section: 'Tenant', icon: 'Car' },
  ],

  // ── Health & Medical ──
  health: [
    { key: 'health_card', label: 'Health card number', type: 'text', placeholder: 'Optional', section: 'Medical', icon: 'CreditCard' },
    { key: 'date_of_birth', label: 'Date of birth', type: 'date', section: 'Medical', icon: 'Calendar' },
    { key: 'allergies', label: 'Allergies', type: 'textarea', placeholder: 'Medications, latex, etc.', section: 'Medical', icon: 'AlertTriangle' },
    { key: 'medications', label: 'Current medications', type: 'textarea', placeholder: 'List current medications...', section: 'Medical', icon: 'Pill' },
    { key: 'emergency_contact', label: 'Emergency contact', type: 'text', placeholder: 'Name + relationship + phone', section: 'Emergency', icon: 'Phone' },
    { key: 'referral_source', label: 'Referred by', type: 'text', placeholder: 'Dr. Smith, walk-in, etc.', section: 'Medical', icon: 'UserPlus' },
  ],
};

/**
 * Get custom field definitions for a business category.
 * Returns empty array for categories without custom fields defined.
 */
export function getClientFields(categoryId: string | null | undefined): ClientFieldDef[] {
  if (!categoryId) return [];
  return CLIENT_FIELDS[categoryId] ?? [];
}

/**
 * Group fields by their section property.
 * Returns sections in the order they first appear in the config.
 */
export function getGroupedClientFields(categoryId: string | null | undefined): { section: string; fields: ClientFieldDef[] }[] {
  const fields = getClientFields(categoryId);
  const map = new Map<string, ClientFieldDef[]>();
  for (const f of fields) {
    const section = f.section ?? 'Other';
    if (!map.has(section)) map.set(section, []);
    map.get(section)!.push(f);
  }
  return Array.from(map.entries()).map(([section, fields]) => ({ section, fields }));
}

/**
 * Get the "key" custom field for list display (subtitle in client lists).
 * Returns the most identifying field for each industry.
 */
export function getClientListSubtitleKey(categoryId: string | null | undefined): string | null {
  if (!categoryId) return null;
  const map: Record<string, string> = {
    beauty: 'preferred_operator',
    pet: 'pet_name',
    automotive: 'vehicle_make',
    property: 'unit_number',
    cleaning: 'property_type',
    wellness: 'experience_level',
    health: 'referral_source',
  };
  return map[categoryId] ?? null;
}
